import axios from "axios";

/**
 * @typedef {Object} LessonItem
 * @property {string} label - La etiqueta del elemento.
 * @property {string} [icon] - El icono del elemento (opcional).
 * @property {string} [id] - El ID del elemento (opcional).
 * @property {string} [state] - El estado del elemento (opcional).
 */

/**
 * @typedef {Object} Lesson
 * @property {string} description - Descripción de lecciones.
 * @property {Array<LessonItem>} items- Lista de lecciones del curso.
 * @property {boolean>} finished- Determina si se completaron los ejercicios de la lección.
 * @property {boolean>} has_initial_practice- Determina si la lección tiene práctica inicial.
 * @property {boolean>} has_final_practice- Determina si la lección tiene práctica final.
 */

/**
 * Obtiene la lista de lecciones de un curso
 * @param {string} courseId ID del curso
 * @returns {Promise.<Lesson>} Lista de lecciones del curso
 */
export async function getLessons(courseId) {
  return axios
    .get(`${import.meta.env.VITE_API_BASE_URL}/lessons/${courseId}`)
    .then((response) => response.data.data)
    .catch((err) => console.error(err));
}


export async function getStudentStatusLessons(courseId) {
  return axios
    .get(`${import.meta.env.VITE_API_BASE_URL}/api/lessons/getStudentStatus`, {
      params: { moduleId: courseId}
    })
    .then((response) => response.data)
    .catch((err) => console.error(err));
}

/**
 * Obtiene las lecciones del curso agrupadas por sección, con estado del estudiante (enrolled, finished).
 * @param {string} courseId ID del curso (moduleId)
 * @returns {Promise.<Array<{id: number, title: string, position: number, lessons: Array}>|null>}
 */
export async function getStudentStatusLessonsBySection(courseId) {
  return axios
    .get(`${import.meta.env.VITE_API_BASE_URL}/api/lessons/getStudentStatusBySection`, {
      params: { moduleId: courseId },
      headers: { Authorization: "Bearer " + localStorage.getItem("token") },
    })
    .then((response) => response.data)
    .catch((err) => {
      console.error(err);
      return null;
    });
}


/**
 * Obtiene los detalle de la lección
 * @param {string} lessonId ID del curso
 * @returns {Promise.<Lesson>} Detalle de la lección
 */
export async function getLesson(lessonId) {
  return axios
    .get(`${import.meta.env.VITE_API_BASE_URL}/api/lessons/${lessonId}`)
    .then((response) => response.data)
    .catch((err) => console.error(err));
}

/**
 * Obtiene la lista de lecciones de un curso
 * @param {string} lessonId ID de la lección
 * @returns {Promise.<Array<LessonItem>>} Lista de ejercicios de la lección
 */
export async function getLessonsExercises(lessonId) {
  return axios
    .get(`${import.meta.env.VITE_API_BASE_URL}/lessons_exercise/${lessonId}`)
    .then((response) => response.data.data)
    .catch((err) => console.error(err));
}

/**
 * Verfiica la respuesta de un ejercio
 * @param {string} exerciseId ID de la lección
 * @param origin El origen del intento (examDiag, practiceDiag, simulacrumDiag)
 * @param originId El ID del origen (examen, práctica o simulacro)
 * @param courseId El ID del curso
 * @returns {Promise.<Object>} Respuesta de la verificación
 */
export async function checkLessonExerciseResponse(
  exerciseId,
  origin,
  originId,
  courseId
) {
  return axios
    .get(`${import.meta.env.VITE_API_BASE_URL}/api/question/getAnswer`, {
      params: {
        exerciseId: exerciseId,
        origin: origin,
        originId: parseInt(originId),
        moduleId: parseInt(courseId),
      }
    })
    .then((response) => response.data)
    .catch((err) => console.error(err));
}

/**
 * Inscribe a un estudiante en una leccion.
 * @param {string} userId ID del estudiante.
 * @param {string} lessonId ID de la leccion.
 * @param {string} courseId ID del curso.
 * @return {Object} Respuesta.
 */
export async function enrollToLesson(lessonId, courseId) {
  return axios
    .post(`${import.meta.env.VITE_API_BASE_URL}/api/lessons/enroll`, {lesson: lessonId, moduleId: courseId})
    .then((response) => response.data)
    .catch((err) => console.error(err));
}


